import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import useUserStore from "../stores/useUserStore";

import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import Select from '../components/ui/Select';
import SegTrack from '../components/ui/SegTrack';
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"/>
  </svg>
);

const FIREFLY_INTERNAL = "__FIREFLY_INTERNAL__";

const DroneOwnerAssignment = ({ droneIds, closeModal, onDirtyChange }) => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const hasCapability = useUserStore((s) => s.hasCapability);
  const isAdmin = hasCapability("admin");

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersError, setCustomersError] = useState("");

  const [selectedValue, setSelectedValue] = useState("");
  const [leaseOrSale, setLeaseOrSale] = useState("lease");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // A chosen owner or a changed lease/sale is work the modal would throw away.
  const dirty = selectedValue !== "" || leaseOrSale !== "lease";
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdmin) {
      setCustomersLoading(false);
      return;
    }
    api.get("/customers")
      .then((data) => {
        setCustomers(Array.isArray(data) ? data : []);
        setCustomersError("");
      })
      .catch((e) => setCustomersError(e?.message || "Failed to load customers"))
      .finally(() => setCustomersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [customers],
  );

  const isFireflyInternal = selectedValue === FIREFLY_INTERNAL;
  const selectedCustomer = customers.find((c) => String(c.id) === selectedValue) || null;

  const summaryText = () => {
    if (isFireflyInternal) {
      return `Return ${droneIds.length} drone${droneIds.length !== 1 ? "s" : ""} to Firefly Internal`;
    }
    if (selectedCustomer) {
      const verb = leaseOrSale === "sale" ? "Sell" : "Assign (lease)";
      return `${verb} ${droneIds.length} drone${droneIds.length !== 1 ? "s" : ""} to ${selectedCustomer.name}`;
    }
    return null;
  };

  const handleSubmit = async () => {
    if (droneIds.length === 0) {
      setError("No drones selected.");
      return;
    }
    if (!selectedValue) {
      setError("Pick a customer or Firefly Internal.");
      return;
    }
    if (!(await confirm({ message: `${summaryText()}?` }))) return;

    setError("");
    setIsProcessing(true);
    try {
      const body = isFireflyInternal
        ? { owner_org_id: null, lease_or_sale: null, drone_serials: droneIds }
        : {
            owner_org_id: Number(selectedValue),
            lease_or_sale: leaseOrSale,
            drone_serials: droneIds,
          };

      const result = await api.post("/fleet/bulk-assign-owner", {
        body,
        onSuccess: () => {},
      });

      const updated = result?.updated?.length ?? 0;
      const unchanged = result?.unchanged?.length ?? 0;
      const missing = result?.missing?.length ?? 0;
      toast.success(
        `Owner assignment: ${updated} updated, ${unchanged} unchanged${missing ? `, ${missing} missing` : ""}`,
      );
      closeModal();
    } catch (err) {
      setError(err?.message || "Failed to update owner assignments.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col">
        <div className="px-6 py-4 border-b border-outline-variant bg-red-900/10">
          <h1 className="text-xl font-bold text-white">Owner Assignment: Admin Only</h1>
        </div>
        <div className="p-6">
          <Alert tone="warning" live={false}>
            Drone ownership writes are restricted to admin accounts. Ask an admin to run this update.
          </Alert>
        </div>
        <div className="px-6 py-4 border-t border-outline-variant bg-surface-container flex justify-end">
          <button
            onClick={closeModal}
            className={buttonClass('quiet', 'md')}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-h-[85vh]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-outline-variant bg-blue-900/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-blue-500/20 flex items-center justify-center text-blue-400">
            <UserIcon />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Owner Assignment</h1>
            <p className="text-on-surface-variant text-sm">
              {droneIds.length} drone{droneIds.length !== 1 ? "s" : ""} selected
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <Reveal show={customersLoading || !!customersError}>
          <FadeIn key={customersLoading ? "loading" : "error"}>
            {customersLoading ? (
              <SkeletonGroup label="Loading customers">
                <Skeleton className="h-5 w-48" />
              </SkeletonGroup>
            ) : (
              <Alert tone="warning">
                Couldn't load customers. Only "Firefly Internal" is available. ({customersError})
              </Alert>
            )}
          </FadeIn>
        </Reveal>

        {/* Owner picker */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-on-surface-variant">Assign to</label>
          <Select
            label="Assign to"
            value={selectedValue || null}
            onChange={setSelectedValue}
            placeholder="Pick one"
            options={[
              { key: FIREFLY_INTERNAL, label: "Firefly Internal (return to pool)" },
              ...sortedCustomers.map((c) => ({ key: String(c.id), label: c.name, group: "Customers" })),
            ]}
          />
        </div>

        {/* Lease/Sale toggle — only when a customer is selected */}
        <Reveal show={!!selectedCustomer}>
          <div className="space-y-2 p-1">
            <label className="block text-sm font-medium text-on-surface-variant">Relationship</label>
            <SegTrack
              label="Relationship"
              size="lg"
              fill
              value={leaseOrSale}
              onChange={setLeaseOrSale}
              options={[
                { key: "lease", label: "Lease", sub: "Firefly retains" },
                { key: "sale", label: "Sale", sub: "Ownership transferred" },
              ]}
            />
          </div>
        </Reveal>

        {/* Summary preview */}
        <Reveal show={!!summaryText()}>
          <Panel on="panel" padding="sm" label="Summary" className="text-sm text-on-surface">
            {summaryText()}
          </Panel>
        </Reveal>

        <Alert tone="error" show={!!error}>{error}</Alert>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-outline-variant bg-surface-container flex justify-end gap-3">
        <button
          onClick={closeModal}
          className={buttonClass('quiet', 'md')}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={droneIds.length === 0 || !selectedValue || isProcessing}
          aria-busy={isProcessing}
          className={buttonClass('filled', 'md')}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <LogoSpinner size="inline" />
              Processing…
            </span>
          ) : (
            `Assign ${droneIds.length} Drone${droneIds.length !== 1 ? "s" : ""}`
          )}
        </button>
      </div>
    </div>
  );
};

export default DroneOwnerAssignment;
